import { useRef } from 'react';
import { FaFacebookSquare, FaLinkedin, FaViber, FaWhatsappSquare } from 'react-icons/fa';
import { FaSquareXTwitter } from 'react-icons/fa6';
import { IoPersonOutline } from 'react-icons/io5';
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  ViberShareButton,
  WhatsappShareButton,
} from 'react-share';
import clsx from 'clsx';
import { useHeroIntroAnimation } from 'common/hook/useHeroIntroAnimation';
import { IBlogItem } from 'common/types/blog';
import { formatDate } from 'common/utils/date';
import { getEmployeeProfileHref } from 'common/utils/employeeProfile';
import { buildAbsoluteUrl } from 'common/utils/url';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import useDimensions from 'use-element-dimensions';

export const HeroBlog: React.FC<{ blog: IBlogItem; seoTitle: string }> = ({ blog, seoTitle }) => {
  const { i18n, t } = useTranslation('common');
  const el = useRef<HTMLElement | null>(null);
  const [containerWidth, ref] = useDimensions(entry => entry.contentRect.width);

  useHeroIntroAnimation({
    scopeRef: el,
    revealAnimations: [
      { delay: 1.8, selector: '#hero-text', toVars: { opacity: 1, translateY: 0 } },
      { delay: 2, selector: '#hero-share', toVars: { opacity: 1, translateY: 0 } },
      { delay: 2.2, selector: '#hero-image', toVars: { opacity: 1, translateY: 0 } },
    ],
  });

  const { author, heroImage, slug } = blog;
  const base = t('common:company.url');
  const url = typeof window === 'undefined' ? '' : buildAbsoluteUrl(base, slug);
  let imageHeight = 0;
  if (heroImage) {
    const imageWidthMultiplier = heroImage.width / containerWidth;
    imageHeight = containerWidth
      ? Math.round(heroImage.height / imageWidthMultiplier)
      : heroImage.height;
  }
  const heightClass = `h-[${imageHeight || 320}px]`;

  const renderAuthorImage = () => {
    if (author.image.src) {
      return (
        <Image
          alt={author.image.alt}
          className="mr-2 h-[30px] w-[30px] rounded-full bg-gray-800 text-black"
          src={author.image.src}
          height={author.image.height}
          width={author.image.width}
        />
      );
    }
    return (
      <div className="mr-2 flex h-[30px] w-[30px] items-center justify-center rounded-full bg-primary/10">
        <IoPersonOutline color="white" size={14} />
      </div>
    );
  };

  const renderAuthor = () => {
    if (author.name === 'Ivan Vukušić') {
      const employeeHref = getEmployeeProfileHref(author.name, i18n.language);
      return (
        <Link href={employeeHref} className="flex items-center text-black">
          {renderAuthorImage()}
          <div>{author.name}</div>
        </Link>
      );
    }
    return (
      <div className="flex items-center text-black">
        {renderAuthorImage()}
        <div>{author.name}</div>
      </div>
    );
  };

  return (
    <section ref={el} className="hero-bg-img flex min-h-[110vh] flex-col justify-between bg-white">
      <div
        ref={ref}
        className="container flex min-h-[60vh] flex-1 flex-col justify-between pt-24 lg:pt-32">
        <div className="flex h-full w-full flex-1 flex-col justify-center">
          <h1
            id="hero-title"
            className="text-heading-hero max-w-[90vw] lg:max-w-[860px]"
            dangerouslySetInnerHTML={{ __html: blog.title }}
          />
        </div>

        <div
          id="hero-share"
          className="flex w-full translate-y-12 flex-col items-end justify-end opacity-0">
          <div className="mb-1 flex min-w-[380px] justify-end border-b-[1px] pb-2 text-sm font-semibold">
            {formatDate(blog.date, 'DD MMMM YYYY', i18n.language)}
          </div>
          <div className="mt-1 flex w-full flex-col items-end md:flex-row md:items-center md:justify-end">
            <div className="flex flex-row items-end">
              <span className="mr-2">{t('share', { defaultValue: 'Share:' })}</span>
              <LinkedinShareButton url={url}>
                <FaLinkedin
                  aria-label={`Blog - ${t('common:share')} LinkedIn - ${seoTitle}`}
                  className=" fill-[#251433ee] transition-all duration-300 hover:fill-primary"
                  size={32}
                />
              </LinkedinShareButton>
              <FacebookShareButton
                aria-label={`Blog - ${t('common:share')} Facebook - ${seoTitle}`}
                url={url}>
                <FaFacebookSquare
                  className=" fill-[#251433ee] transition-all duration-300 hover:fill-primary"
                  fill="black"
                  size={32}
                />
              </FacebookShareButton>
              <TwitterShareButton
                aria-label={`Blog - ${t('common:share')} Twitter - ${seoTitle}`}
                url={url}>
                <FaSquareXTwitter
                  className=" fill-[#251433ee] transition-all duration-300 hover:fill-primary"
                  fill="black"
                  size={32}
                />
              </TwitterShareButton>
              <ViberShareButton
                aria-label={`Blog - ${t('common:share')} Viber - ${seoTitle}`}
                url={url}>
                <div className=" mb-[2px] flex h-[28px] w-[28px] items-center justify-center rounded-[4px] bg-[#251433ee] transition-all duration-300 hover:bg-primary">
                  <FaViber fill="white" size={20} />
                </div>
              </ViberShareButton>
              <WhatsappShareButton
                aria-label={`Blog - ${t('common:share')} Whatsapp - ${seoTitle}`}
                url={url}>
                <FaWhatsappSquare
                  className=" fill-[#251433ee] transition-all duration-300 hover:fill-primary"
                  fill="black"
                  size={32}
                />
              </WhatsappShareButton>
            </div>
            <div className="flex flex-row items-center gap-2 md:pl-8">
              {renderAuthor()}
              {!!blog.author.url && (
                <a href={blog.author.url} target="_blank" rel="noreferrer">
                  <FaLinkedin
                    aria-label={`Blog - ${t('common:authorLinkedin')} LinkedIn - ${seoTitle}`}
                    className=" fill-[#251433ee] transition-all duration-300 hover:fill-primary"
                    size={32}
                  />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="container z-50">
        <div
          id="hero-image"
          className={clsx(
            `relative z-50 -mb-4 mt-4 min-h-[350px!important] w-full translate-y-12 bg-white opacity-0 lg:-mb-10`,
            { [heightClass]: !!imageHeight && imageHeight > 0 },
          )}>
          {!!heroImage?.src && (
            <Image
              className={clsx('min-h-[350px!important] w-full object-cover object-bottom', {
                [heightClass]: !!imageHeight && imageHeight > 0,
              })}
              alt={heroImage?.alt}
              loading="lazy"
              src={heroImage?.src}
              height={heroImage.height}
              width={heroImage.width}
              sizes="100vw"
            />
          )}
        </div>
      </div>
    </section>
  );
};
